import $ from 'jquery';
import Swiper, { Scrollbar, Lazy } from 'swiper';
import { debounce } from '@github/mini-throttle';
import { DESKTOP_GRID_COLUMNS, isMobile as isMobileForRoot, MOBILE_GRID_COLUMNS, movePromoTiles, resetElementPositions, savePositions } from './promo-tiles-utils';
// ---- constants ----
const SWIPER_SLIDE_GAP = 16;
const RESIZE_DEBOUNCE_TIME = 250;
const SWIPER_UPDATE_DELAY = 50;
const DESKTOP_SELECTOR = '.js-promo-tiles-desktop';
const MOBILE_SELECTOR = '.js-promo-tiles-mobile';
const MOBILE_CONTAINER_SELECTOR = '.js-promo-tiles-mobile-container';
const PRODUCT_GRID_SELECTOR = '.js-product-grid > li';
const PRODUCT_GRID_ANCHOR = '[data-id]' // <ul id="product-grid" data-id="{{ section.id }}">
;
// Register Swiper modules once
Swiper.use([
    Lazy,
    Scrollbar
]);
// One Swiper map per section root
const tilesSwipersByRoot = new WeakMap();
// Per-root lifecycle registry (for cleanup on section unload)
const registry = new Map();
// --- helpers ---
const getRootFromAny = (el)=>{
    const section = el === null || el === void 0 ? void 0 : el.closest('.shopify-section');
    if (section && section instanceof HTMLElement) {
        return section;
    }
    const docSection = document.querySelector('.shopify-section');
    if (docSection && docSection instanceof HTMLElement) {
        return docSection;
    }
    return document.body;
};
const $within = (root, sel)=>$(root).find(sel);
const showText = ($currentTile, $section)=>{
    const $promoTiles = $section.find('.js-promo-tile');
    $promoTiles.not($currentTile).removeClass('tile--active');
    $currentTile.addClass('tile--active');
};
// Desktop hover behavior (scoped)
const initPromoTilesDesktop = (root)=>{
    const sections = $within(root, DESKTOP_SELECTOR);
    // Remove previous listeners
    sections.find('.js-promo-tile-image').off('mouseenter mouseleave');
    sections.each((_, sElement)=>{
        const $section = $(sElement);
        const $tiles = $section.find('.js-promo-tile');
        if (!$tiles.length) return;
        $tiles.find('.js-promo-tile-image').each((_i, el)=>{
            $(el).on('mouseenter', function() {
                const $tile = $(this).closest('.js-promo-tile');
                showText($tile, $section);
            });
            $(el).on('mouseleave', function() {
                $tiles.find('.promo-tiles__text').removeClass('promo-tile--active');
            });
        });
    });
};
// Mobile swiper (scoped)
const initPromoTilesMobile = (root)=>{
    var _tilesSwipersByRoot_get;
    // Destroy existing instances in this root
    const swipers = (_tilesSwipersByRoot_get = tilesSwipersByRoot.get(root)) !== null && _tilesSwipersByRoot_get !== void 0 ? _tilesSwipersByRoot_get : new Map();
    swipers.forEach((sw)=>sw.destroy(true, true));
    swipers.clear();
    const sections = $within(root, MOBILE_SELECTOR);
    sections.each((_, sElement)=>{
        const $s = $(sElement);
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const swiperContainerElement = $s.find(`${MOBILE_CONTAINER_SELECTOR} .swiper-container`).first().get(0);
        if (!swiperContainerElement) return;
        const indexStorage = sessionStorage;
        const activeIndexKey = `promo-tiles-${window.location.pathname}-activeIndex`;
        const initialSlide = parseInt(indexStorage.getItem(activeIndexKey) || '0');
        const scrollbarEl = $s.find('.swiper-scrollbar').get(0) || '.js-promo-tiles-mobile .swiper-scrollbar';
        const options = {
            autoHeight: true,
            direction: 'horizontal',
            loop: false,
            slidesPerView: 1.53,
            spaceBetween: SWIPER_SLIDE_GAP,
            watchSlidesProgress: true,
            slidesOffsetAfter: SWIPER_SLIDE_GAP,
            slidesOffsetBefore: 0,
            initialSlide,
            scrollbar: {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
                el: scrollbarEl,
                draggable: true,
                hide: false
            },
            history: {
                replaceState: true
            },
            on: {
                slideChange (swiper) {
                    indexStorage.setItem(activeIndexKey, swiper.activeIndex.toString());
                }
            }
        };
        const swiper = new Swiper(swiperContainerElement, options);
        swipers.set(swiperContainerElement, swiper);
    });
    tilesSwipersByRoot.set(root, swipers);
};
// Per-section setup & cleanup
const setupForContainer = async (root)=>{
    if (registry.has(root)) return;
    const saveInitialState = async ()=>{
        const $desktop = $within(root, DESKTOP_SELECTOR);
        const $mobile = $within(root, MOBILE_SELECTOR);
        const dh = $desktop.is(':hidden');
        if (dh) $desktop.show();
        const mh = $mobile.is(':hidden');
        if (mh) $mobile.show();
        savePositions('desktop-tile', DESKTOP_SELECTOR, root);
        savePositions('mobile-tile', MOBILE_SELECTOR, root);
        if (dh) $desktop.hide();
        if (mh) $mobile.hide();
    };
    const applyLayout = async ()=>{
        resetElementPositions(DESKTOP_SELECTOR, root);
        resetElementPositions(MOBILE_SELECTOR, root);
        if (isMobileForRoot(root)) {
            $within(root, DESKTOP_SELECTOR).hide();
            $within(root, MOBILE_SELECTOR).show();
            initPromoTilesMobile(root);
            movePromoTiles(MOBILE_SELECTOR, PRODUCT_GRID_SELECTOR, MOBILE_GRID_COLUMNS, root, true);
            await new Promise((r)=>{
                return setTimeout(()=>{
                    var _tilesSwipersByRoot_get;
                    (_tilesSwipersByRoot_get = tilesSwipersByRoot.get(root)) === null || _tilesSwipersByRoot_get === void 0 ? void 0 : _tilesSwipersByRoot_get.forEach((sw)=>sw.update());
                    r(null);
                }, SWIPER_UPDATE_DELAY);
            });
        } else {
            $within(root, MOBILE_SELECTOR).hide();
            $within(root, DESKTOP_SELECTOR).show();
            movePromoTiles(DESKTOP_SELECTOR, PRODUCT_GRID_SELECTOR, DESKTOP_GRID_COLUMNS, root, false);
            initPromoTilesDesktop(root);
        }
    };
    await saveInitialState();
    await applyLayout();
    const onResize = debounce(()=>{
        void applyLayout();
    }, RESIZE_DEBOUNCE_TIME);
    window.addEventListener('resize', onResize);
    registry.set(root, {
        cleanup: ()=>{
            window.removeEventListener('resize', onResize);
            const swipers = tilesSwipersByRoot.get(root);
            swipers === null || swipers === void 0 ? void 0 : swipers.forEach((sw)=>sw.destroy(true, true));
            swipers === null || swipers === void 0 ? void 0 : swipers.clear();
            tilesSwipersByRoot.delete(root);
        }
    });
};
const cleanupForContainer = (root)=>{
    const entry = registry.get(root);
    if (!entry) return;
    entry.cleanup();
    registry.delete(root);
};
export const init = async ()=>{
    // Theme Editor re-render hooks (always bind)
    document.addEventListener('shopify:section:load', (e)=>{
        const target = e.target;
        const root = target instanceof HTMLElement ? getRootFromAny(target) : getRootFromAny(null);
        void setupForContainer(root);
    });
    document.addEventListener('shopify:section:unload', (e)=>{
        const target = e.target;
        const root = target instanceof HTMLElement ? getRootFromAny(target) : getRootFromAny(null);
        cleanupForContainer(root);
    });
    // Initial boot for already-present instances
    const boot = ()=>{
        document.querySelectorAll(PRODUCT_GRID_ANCHOR).forEach((anchor)=>{
            const root = getRootFromAny(anchor);
            void setupForContainer(root);
        });
    };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
    // Optional global cleanup
    return ()=>{
        document.querySelectorAll(PRODUCT_GRID_ANCHOR).forEach((anchor)=>{
            cleanupForContainer(getRootFromAny(anchor));
        });
    };
};
